import { inferAdditionalFields, usernameClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

import { ROLES } from '@shared/schemas/user';

/*
 * The browser half of better-auth.
 *
 * **Nothing here holds a token, and that is the point.** The session lives in an
 * HTTP-only cookie the browser attaches by itself; this module only calls
 * endpoints and reads back a user object. The prototype kept `auth_token` *and*
 * `refresh_token` in `localStorage`, so a single XSS gave permanent account
 * access (P21). There is nothing equivalent to reach for here, because the
 * server puts no token in any response body either.
 *
 * The extra user fields are declared rather than imported from the server
 * config. `inferAdditionalFields<typeof auth>` would be less to write, but it
 * means the browser bundle's type graph reaches into src/server — and the one
 * thing standing between a type-only import and a runtime one is a keyword.
 * `ROLES` comes from src/shared, which both sides are meant to share, so the
 * only thing restated below is the shape.
 */

export const authClient = createAuthClient({
  // Same origin. In development Vite proxies /api to the Express process, so
  // this is right in both environments without a build-time switch.
  basePath: '/api/auth',
  plugins: [
    usernameClient(),
    inferAdditionalFields({
      user: {
        role: { type: [...ROLES] },
        isActive: { type: 'boolean' },
        recoveryEmail: { type: 'string', required: false },
        lastLoginAt: { type: 'date', required: false },
      },
    }),
  ],
});

/*
 * `requestPasswordReset`, not `forgetPassword` — better-auth renamed it, and
 * the older name is what most search results still show. Re-exported here so
 * the pages import from one place and a future rename is a one-line change.
 */
export const { useSession, signIn, signOut, requestPasswordReset, resetPassword, changePassword } =
  authClient;
